import React, { useState } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, Circle, AlertCircle, Upload, Loader, X, CheckCircle2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import Tesseract from 'tesseract.js';
import { formatDateDisplay, formatDateForInput } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';

/**
 * DocumentUploadWizard - Sequential document upload with validation
 * 
 * Features:
 * - Step-by-step document upload
 * - OCR processing with fallback to manual entry
 * - Field validation per document type
 * - Cannot proceed until current step complete
 * - Progress tracking
 * - Option to skip to manual entry at any step
 */
const DocumentUploadWizard = ({ onComplete, onCancel, editingId, lessees = [], onSkipToManual }) => {
  const { isMobile, isTablet } = useDeviceDetection();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [wizardData, setWizardData] = useState({
    emiratesId: {},
    passport: {},
    license: {},
    photo: null
  });

  const steps = [
    {
      id: 'emiratesId',
      title: 'Emirates ID',
      description: 'Upload and verify Emirates ID',
      required: true,
      icon: '🪪'
    },
    {
      id: 'passport',
      title: 'Passport',
      description: 'Upload and verify Passport',
      required: true,
      icon: '🛂'
    },
    {
      id: 'license',
      title: 'Driving License',
      description: 'Upload and verify Driving License',
      required: true,
      icon: '🚗'
    },
    {
      id: 'photo',
      title: 'Profile Photo',
      description: 'Upload profile photo (optional)',
      required: false,
      icon: '📸'
    }
  ];

  const handleStepComplete = (stepId, data) => {
    // Validate cross-document consistency before proceeding
    const validationError = validateCrossDocumentData(stepId, data);
    if (validationError) {
      toast.error(validationError, {
        duration: 8000,
        style: {
          background: '#FEE2E2',
          border: '2px solid #DC2626',
          padding: '16px',
          maxWidth: '600px'
        }
      });
      return; // Don't proceed to next step
    }

    // Update wizard data
    setWizardData(prev => ({
      ...prev,
      [stepId]: data
    }));

    // Mark step as completed
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }

    // Move to next step or complete wizard
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      toast.success(`${steps[currentStep].title} completed!`);
    } else {
      // All steps complete
      handleWizardComplete();
    }
  };

  // Validate that all documents belong to the same person
  const validateCrossDocumentData = (currentStepId, currentData) => {
    // Get all previously completed documents
    const previousDocs = [];
    
    if (wizardData.emiratesId?.name && currentStepId !== 'emiratesId') {
      previousDocs.push({ type: 'Emirates ID', ...wizardData.emiratesId });
    }
    if (wizardData.passport?.name && currentStepId !== 'passport') {
      previousDocs.push({ type: 'Passport', ...wizardData.passport });
    }
    if (wizardData.license?.name && currentStepId !== 'license') {
      previousDocs.push({ type: 'Driving License', ...wizardData.license });
    }

    // Skip validation if no previous documents or current is photo
    if (previousDocs.length === 0 || currentStepId === 'photo') {
      return null;
    }

    const currentDocType = {
      emiratesId: 'Emirates ID',
      passport: 'Passport',
      license: 'Driving License'
    }[currentStepId];

    // Validate name consistency
    if (currentData.name) {
      for (const prevDoc of previousDocs) {
        if (prevDoc.name) {
          const nameMismatch = !areNamesSimilar(currentData.name, prevDoc.name);
          if (nameMismatch) {
            return `⚠️ NAME MISMATCH DETECTED!\n\n${currentDocType}: "${currentData.name}"\n${prevDoc.type}: "${prevDoc.name}"\n\n❌ All documents must belong to the SAME person.\n\n✓ Please re-upload the correct ${currentDocType} or check if you uploaded the wrong document.`;
          }
        }
      }
    }

    // Validate date of birth consistency
    // IMPORTANT: Only validate DOB if both documents have it AND the current document is NOT a driving license
    // Driving license DOB extraction is unreliable - issue dates are often mistaken for DOB
    if (currentData.date_of_birth && currentStepId !== 'license') {
      for (const prevDoc of previousDocs) {
        // Skip comparison with driving license DOB (unreliable)
        if (prevDoc.type === 'Driving License') continue;
        
        if (prevDoc.date_of_birth) {
          if (currentData.date_of_birth !== prevDoc.date_of_birth) {
            return `⚠️ DATE OF BIRTH MISMATCH DETECTED!\n\n${currentDocType}: ${formatDateDisplay(currentData.date_of_birth)}\n${prevDoc.type}: ${formatDateDisplay(prevDoc.date_of_birth)}\n\n❌ All documents must belong to the SAME person.\n\n✓ Please re-upload the correct ${currentDocType} or verify the dates are accurate.`;
          }
        }
      }
    }

    return null; // Validation passed
  };

  // Helper function to check if names are similar (accounting for variations)
  const areNamesSimilar = (name1, name2) => {
    // Normalize names: lowercase, trim, remove extra spaces
    const normalize = (name) => name.toLowerCase().trim().replace(/\s+/g, ' ');
    
    const n1 = normalize(name1);
    const n2 = normalize(name2);

    // Exact match
    if (n1 === n2) return true;

    // Split into parts
    const parts1 = n1.split(' ');
    const parts2 = n2.split(' ');

    // Check if first name and last name match (common variations)
    const firstName1 = parts1[0];
    const lastName1 = parts1[parts1.length - 1];
    const firstName2 = parts2[0];
    const lastName2 = parts2[parts2.length - 1];

    // If both first names and last names match, consider it the same person
    if (firstName1 === firstName2 && lastName1 === lastName2) return true;

    // If one name is contained in the other (e.g., "John Smith" vs "John Michael Smith")
    if (n1.includes(n2) || n2.includes(n1)) return true;

    // Check if at least 2 name parts match
    const matchingParts = parts1.filter(p => parts2.includes(p)).length;
    if (matchingParts >= 2) return true;

    return false; // Names don't match
  };

  const handleWizardComplete = () => {
    toast.success('All documents uploaded successfully!');
    
    // Debug: Log wizard data before consolidation
    console.log('=== WIZARD DATA DEBUG ===');
    console.log('Emirates ID data:', wizardData.emiratesId);
    console.log('Passport data:', wizardData.passport);
    console.log('License data:', wizardData.license);
    console.log('Photo data:', wizardData.photo);
    
    // Consolidate all data
    const consolidatedData = {
      // Emirates ID data (front side)
      name: wizardData.emiratesId.name || '',
      date_of_birth: wizardData.emiratesId.date_of_birth || '',
      nationality: wizardData.emiratesId.nationality || '',
      emirates_id: wizardData.emiratesId.emirates_id || '',
      emirates_id_issue_date: wizardData.emiratesId.emirates_id_issue_date || '',
      emirates_id_expiry: wizardData.emiratesId.emirates_id_expiry || '',
      // Emirates ID data (back side - new fields)
      occupation: wizardData.emiratesId.occupation || '',
      sponsor: wizardData.emiratesId.sponsor || '',
      place_of_issue: wizardData.emiratesId.place_of_issue || '',
      // Employer (from lessee dropdown)
      employer: wizardData.emiratesId.employer || '',
      lessee_id: wizardData.emiratesId.lessee_id || '',
      lessee_name: wizardData.emiratesId.lessee_name || '',
      // Emirates ID document URLs (front and back)
      emirates_id_front_url: wizardData.emiratesId.emirates_id_front_url || '',
      emirates_id_back_url: wizardData.emiratesId.emirates_id_back_url || '',
      emirates_id_doc_url: wizardData.emiratesId.emirates_id_front_url || '', // For backward compatibility
      
      // Passport data
      passport_number: wizardData.passport.passport_number || '',
      passport_issue_date: wizardData.passport.passport_issue_date || '',
      passport_expiry: wizardData.passport.passport_expiry || '',
      passport_doc_url: wizardData.passport.document_url || '',
      
      // License data
      license_number: wizardData.license.license_number || '',
      license_issue_date: wizardData.license.license_issue_date || '',
      license_expiry: wizardData.license.license_expiry || '',
      license_doc_url: wizardData.license.document_url || '',
      
      // Photo
      photo_url: wizardData.photo?.document_url || ''
    };
    
    // Debug: Log consolidated data
    console.log('=== CONSOLIDATED DATA ===');
    console.log('license_doc_url:', consolidatedData.license_doc_url);
    console.log('passport_doc_url:', consolidatedData.passport_doc_url);
    console.log('emirates_id_doc_url:', consolidatedData.emirates_id_doc_url);
    
    onComplete(consolidatedData);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipOptional = () => {
    if (!steps[currentStep].required) {
      handleStepComplete(steps[currentStep].id, null);
    }
  };

  const getStepStatus = (index) => {
    if (completedSteps.includes(index)) return 'completed';
    if (index === currentStep) return 'current';
    return 'pending';
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="w-full max-w-4xl mx-auto px-1">
      {/* Progress Header */}
      <Card className="mb-4 sm:mb-6">
        <CardHeader className="p-3 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <CardTitle className={`${isMobile ? 'text-lg' : 'text-2xl'}`}>
              {isMobile ? 'Upload Documents' : 'Upload Rider Documents'}
            </CardTitle>
            <Badge variant="outline" className="text-xs sm:text-sm">
              {currentStep + 1}/{steps.length}
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-slate-200 rounded-full h-1.5 sm:h-2 mb-3 sm:mb-4">
            <div 
              className="bg-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Steps Indicator - Simplified on mobile */}
          {isMobile ? (
            <div className="flex items-center justify-center gap-2">
              {steps.map((step, index) => {
                const status = getStepStatus(index);
                return (
                  <div 
                    key={step.id} 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs
                      ${status === 'completed' ? 'bg-green-500 text-white' :
                        status === 'current' ? 'bg-blue-500 text-white' :
                        'bg-slate-200 text-slate-400'}
                    `}
                  >
                    {status === 'completed' ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-between overflow-x-auto">
              {steps.map((step, index) => {
                const status = getStepStatus(index);
                return (
                  <div key={step.id} className="flex items-center flex-shrink-0">
                    <div className="flex flex-col items-center">
                      <div className={`
                        w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-1 sm:mb-2 transition-all
                        ${status === 'completed' ? 'bg-green-500 text-white' :
                          status === 'current' ? 'bg-blue-500 text-white' :
                          'bg-slate-200 text-slate-400'}
                      `}>
                        {status === 'completed' ? (
                          <Check className="w-5 h-5 sm:w-6 sm:h-6" />
                        ) : status === 'current' ? (
                          <Circle className="w-5 h-5 sm:w-6 sm:h-6 fill-current" />
                        ) : (
                          <Circle className="w-5 h-5 sm:w-6 sm:h-6" />
                        )}
                      </div>
                      <div className="text-center">
                        <div className={`text-xs sm:text-sm font-medium whitespace-nowrap ${
                          status === 'current' ? 'text-blue-600' : 
                          status === 'completed' ? 'text-green-600' : 
                          'text-slate-400'
                        }`}>
                          {step.icon} {isTablet ? step.title.split(' ')[0] : step.title}
                        </div>
                        {!step.required && !isMobile && (
                          <Badge variant="secondary" className="text-xs mt-1">Optional</Badge>
                        )}
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`h-0.5 w-8 sm:w-16 mx-1 sm:mx-2 ${
                        completedSteps.includes(index) ? 'bg-green-500' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Current Step Content */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="flex items-center gap-2">
            <span className={`${isMobile ? 'text-2xl' : 'text-3xl'}`}>{currentStepData.icon}</span>
            <div>
              <div className={`${isMobile ? 'text-base' : 'text-lg'}`}>{currentStepData.title}</div>
              <div className="text-xs sm:text-sm text-slate-500 font-normal mt-1">
                {currentStepData.description}
              </div>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent>
          {/* Step-specific component will be rendered here */}
          {currentStepData.id === 'emiratesId' && (
            <EmiratesIdStep
              data={wizardData.emiratesId}
              onComplete={(data) => handleStepComplete('emiratesId', data)}
              editingId={editingId}
              wizardData={wizardData}
              lessees={lessees}
            />
          )}

          {currentStepData.id === 'passport' && (
            <PassportStep
              data={wizardData.passport}
              onComplete={(data) => handleStepComplete('passport', data)}
              editingId={editingId}
              wizardData={wizardData}
            />
          )}

          {currentStepData.id === 'license' && (
            <LicenseStep
              data={wizardData.license}
              onComplete={(data) => handleStepComplete('license', data)}
              editingId={editingId}
              wizardData={wizardData}
            />
          )}

          {currentStepData.id === 'photo' && (
            <PhotoStep
              data={wizardData.photo}
              onComplete={(data) => handleStepComplete('photo', data)}
              editingId={editingId}
            />
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t">
            <div>
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  type="button"
                >
                  ← Back to {steps[currentStep - 1].title}
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {/* Skip to Manual Entry Option */}
              {onSkipToManual && (
                <Button
                  variant="ghost"
                  onClick={onSkipToManual}
                  type="button"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  Skip All & Enter Manually
                </Button>
              )}
              
              {!currentStepData.required && (
                <Button
                  variant="ghost"
                  onClick={handleSkipOptional}
                  type="button"
                >
                  Skip (Optional)
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={onCancel}
                type="button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Important:</strong> You must complete each step before proceeding to the next. 
            If OCR cannot read your document, you&apos;ll be asked to enter the information manually.
          </div>
        </div>
      </div>

      {/* Cross-Document Validation Warning */}
      {currentStep > 0 && (
        <div className="mt-3 p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <strong>⚠️ Document Verification:</strong> All documents must belong to the SAME person. 
              The system will automatically verify that the name and date of birth match across all uploaded documents.
              {wizardData.emiratesId?.name && (
                <div className="mt-2 text-xs">
                  <strong>Verified Name:</strong> {wizardData.emiratesId.name}
                  {wizardData.emiratesId.date_of_birth && (
                    <> | <strong>DOB:</strong> {formatDateDisplay(wizardData.emiratesId.date_of_birth)}</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Step Components

// Emirates ID Step Component - Supports Front and Back Upload
const EmiratesIdStep = ({ data, onComplete, editingId, wizardData, lessees = [] }) => {
  // Separate states for front and back uploads
  const [frontUploading, setFrontUploading] = useState(false);
  const [backUploading, setBackUploading] = useState(false);
  const [frontOcrProcessing, setFrontOcrProcessing] = useState(false);
  const [backOcrProcessing, setBackOcrProcessing] = useState(false);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontUrl, setFrontUrl] = useState(data?.emirates_id_front_url || '');
  const [backUrl, setBackUrl] = useState(data?.emirates_id_back_url || '');
  const [extractedData, setExtractedData] = useState(data || {});
  const [manualEntry, setManualEntry] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationFailed, setValidationFailed] = useState(false);
  const [employerSearch, setEmployerSearch] = useState('');

  // Filter lessees based on search
  const filteredLessees = lessees.filter(lessee => 
    lessee.company_name?.toLowerCase().includes(employerSearch.toLowerCase()) ||
    lessee.owner_name?.toLowerCase().includes(employerSearch.toLowerCase())
  );

  const handleFrontFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!validateDocument(selectedFile)) return;

    setFrontFile(selectedFile);
    setFrontUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('entity_type', 'rider');
      uploadFormData.append('document_type', 'emirates_id_front');
      
      if (editingId) {
        uploadFormData.append('entity_id', editingId);
      }

      const uploadResponse = await axios.post(
        `${API}/upload`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000
        }
      );

      const serverFileUrl = uploadResponse.data.url;
      setFrontUrl(serverFileUrl);
      setFrontUploading(false);

      // Process OCR for front side
      await processFrontOCR(selectedFile, serverFileUrl);

    } catch (error) {
      console.error('Front upload failed:', error);
      toast.error('File upload failed. Please try again.');
      setFrontUploading(false);
      setFrontFile(null);
    }
  };

  const handleBackFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!validateDocument(selectedFile)) return;

    setBackFile(selectedFile);
    setBackUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('entity_type', 'rider');
      uploadFormData.append('document_type', 'emirates_id_back');
      
      if (editingId) {
        uploadFormData.append('entity_id', editingId);
      }

      const uploadResponse = await axios.post(
        `${API}/upload`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000
        }
      );

      const serverFileUrl = uploadResponse.data.url;
      setBackUrl(serverFileUrl);
      setBackUploading(false);

      // Process OCR for back side
      await processBackOCR(selectedFile, serverFileUrl);

    } catch (error) {
      console.error('Back upload failed:', error);
      toast.error('File upload failed. Please try again.');
      setBackUploading(false);
      setBackFile(null);
    }
  };

  const processFrontOCR = async (file, url) => {
    setFrontOcrProcessing(true);
    const processingToast = toast.loading('🚀 AI Vision scanning Emirates ID Front...');
    const OCR_TIMEOUT = 30000; // 30 seconds timeout

    try {
      // Use new Vision OCR API (GPT-4 Vision - much faster)
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/ocr/vision/emirates-id-front`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        },
        timeout: OCR_TIMEOUT
      });

      toast.dismiss(processingToast);

      if (response.data.success && response.data.data) {
        const ocrData = response.data.data;
        
        // Map OCR response to our format
        const parsed = {
          name: ocrData.name || '',
          nationality: ocrData.nationality || '',
          emirates_id: ocrData.emirates_id || '',
          date_of_birth: ocrData.date_of_birth || '',
          gender: ocrData.gender || '',
          expiry_date: ocrData.expiry_date || ''
        };
        
        // Validate against previous docs
        const validationError = validateAgainstPreviousDocs(parsed, wizardData, 'Emirates ID');
        
        if (validationError) {
          setValidationFailed(true);
          setExtractedData(prev => ({ ...prev, emirates_id_front_url: url }));
          toast.error('Document verification failed', {
            description: validationError,
            duration: 10000,
            style: {
              background: '#FEE2E2',
              border: '2px solid #DC2626',
              padding: '16px'
            }
          });
          return;
        }

        setValidationFailed(false);
        setExtractedData(prev => ({
          ...prev,
          ...parsed,
          emirates_id_front_url: url
        }));
        
        if (parsed.name || parsed.emirates_id) {
          toast.success('✅ Emirates ID Front scanned successfully!');
        } else {
          setManualEntry(true);
          toast.warning('Unable to extract data from front. Please enter manually.');
        }
      } else {
        throw new Error(response.data.error || 'OCR failed');
      }

    } catch (error) {
      console.error('Front OCR failed:', error);
      toast.dismiss(processingToast);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('⏱️ OCR timed out after 30 seconds. Please try again.');
      } else {
        toast.error('OCR scanning failed. Please enter information manually.');
      }
      setManualEntry(true);
      setExtractedData(prev => ({ ...prev, emirates_id_front_url: url }));
    } finally {
      setFrontOcrProcessing(false);
    }
  };

  const processBackOCR = async (file, url) => {
    setBackOcrProcessing(true);
    const processingToast = toast.loading('🚀 AI Vision scanning Emirates ID Back...');
    const OCR_TIMEOUT = 30000; // 30 seconds timeout

    try {
      // Use new Vision OCR API (GPT-4 Vision - much faster)
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/ocr/vision/emirates-id-back`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        },
        timeout: OCR_TIMEOUT
      });

      toast.dismiss(processingToast);

      if (response.data.success && response.data.data) {
        const ocrData = response.data.data;
        
        // Map OCR response to our format
        const parsed = {
          occupation: ocrData.occupation || '',
          employer: ocrData.employer || '',
          place_of_issue: ocrData.place_of_issue || '',
          sponsor: ocrData.sponsor || ''
        };
        
        setExtractedData(prev => ({
          ...prev,
          ...parsed,
          emirates_id_back_url: url
        }));
        
        if (parsed.employer || parsed.occupation) {
          toast.success('✅ Emirates ID Back scanned successfully!');
        } else {
          toast.warning('Unable to extract data from back. Please enter manually.');
        }
      } else {
        throw new Error(response.data.error || 'OCR failed');
      }

    } catch (error) {
      console.error('Back OCR failed:', error);
      toast.dismiss(processingToast);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('⏱️ OCR timed out after 30 seconds. Please try again.');
      } else {
        toast.error('OCR scanning failed for back side. Please enter information manually.');
      }
      setExtractedData(prev => ({ ...prev, emirates_id_back_url: url }));
    } finally {
      setBackOcrProcessing(false);
    }
  };

  const validateDocument = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file format! Please upload JPG or PNG images only.');
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large! Maximum size: 10MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return false;
    }

    const minSize = 50 * 1024;
    if (file.size < minSize) {
      toast.error('File too small! Minimum size: 50KB. Please upload a higher quality image.');
      return false;
    }

    return true;
  };

  const handleFieldChange = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleEmployerSelect = (lesseeId) => {
    const lessee = lessees.find(l => l.id === lesseeId);
    if (lessee) {
      setExtractedData(prev => ({
        ...prev,
        lessee_id: lessee.id,
        lessee_name: lessee.company_name,
        employer: lessee.company_name
      }));
      setEmployerSearch('');
      if (errors.employer) {
        setErrors(prev => ({ ...prev, employer: null }));
      }
    }
  };

  const validateFields = () => {
    const newErrors = {};

    if (!extractedData.emirates_id) {
      newErrors.emirates_id = 'Emirates ID is required';
    } else if (!/^784-\d{4}-\d{7}-\d$/.test(extractedData.emirates_id)) {
      newErrors.emirates_id = 'Invalid format (784-YYYY-XXXXXXX-X)';
    }

    if (!extractedData.name || extractedData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    if (!extractedData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    }

    if (!extractedData.emirates_id_expiry) {
      newErrors.emirates_id_expiry = 'Expiry date is required';
    } else {
      const expiryDate = new Date(extractedData.emirates_id_expiry);
      if (expiryDate < new Date()) {
        newErrors.emirates_id_expiry = 'Emirates ID has expired';
      }
    }

    // Employer is mandatory - must be from pre-registered lessees
    if (!extractedData.lessee_id) {
      newErrors.employer = 'Employer is required (select from pre-registered lessees)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleComplete = () => {
    if (validationFailed) {
      toast.error('Please re-upload a valid document');
      return;
    }

    // Check if both front and back are uploaded
    const hasFront = frontFile || frontUrl || extractedData.emirates_id_front_url;
    const hasBack = backFile || backUrl || extractedData.emirates_id_back_url;
    
    if (!hasFront) {
      toast.error('Please upload Emirates ID Front');
      return;
    }
    
    if (!hasBack) {
      toast.error('Please upload Emirates ID Back');
      return;
    }

    if (!validateFields()) {
      toast.error('Please fix the errors before continuing');
      return;
    }

    const completeData = {
      ...extractedData,
      emirates_id_front_url: frontUrl || extractedData.emirates_id_front_url || '',
      emirates_id_back_url: backUrl || extractedData.emirates_id_back_url || ''
    };
    onComplete(completeData);
  };

  const isProcessing = frontUploading || backUploading || frontOcrProcessing || backOcrProcessing;
  const bothUploaded = (frontFile || frontUrl) && (backFile || backUrl);

  return (
    <div className="space-y-6" style={{ cursor: isProcessing ? 'wait' : 'default' }}>
      {/* Upload Instructions */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Emirates ID Upload:</strong> Please upload both front and back sides of the Emirates ID.
            <ul className="mt-2 list-disc list-inside text-xs">
              <li><strong>Front side:</strong> Contains name, Emirates ID number, nationality, DOB, expiry date</li>
              <li><strong>Back side:</strong> Contains occupation, sponsor, place of issue</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Two-Column Upload Section */}
      <div className="grid grid-cols-2 gap-4">
        {/* Front Side Upload */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold flex items-center gap-2">
            🪪 Emirates ID - Front Side *
            {(frontFile || frontUrl) && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </Label>
          
          {!frontFile && !frontUrl ? (
            <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
              <input
                type="file"
                id="emirates-id-front-upload"
                accept="image/*"
                className="hidden"
                onChange={handleFrontFileChange}
                disabled={isProcessing}
              />
              
              {frontUploading || frontOcrProcessing ? (
                <div className="flex flex-col items-center gap-2 py-2">
                  <Loader className="w-8 h-8 text-purple-500 animate-spin" />
                  <div className="text-xs font-medium text-purple-700">
                    {frontUploading ? 'Uploading...' : 'Scanning...'}
                  </div>
                </div>
              ) : (
                <>
                  <Camera className="w-10 h-10 text-purple-400 mx-auto mb-2" />
                  <label
                    htmlFor="emirates-id-front-upload"
                    className="cursor-pointer text-sm text-purple-600 hover:text-purple-700 font-medium block"
                  >
                    Upload Front
                  </label>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG (max 10MB)</p>
                </>
              )}
            </div>
          ) : (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-700">Front uploaded</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFrontFile(null);
                    setFrontUrl('');
                    setExtractedData(prev => {
                      const { emirates_id_front_url, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="text-xs text-gray-500 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Back Side Upload */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold flex items-center gap-2">
            🪪 Emirates ID - Back Side *
            {(backFile || backUrl) && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </Label>
          
          {!backFile && !backUrl ? (
            <div className="border-2 border-dashed border-indigo-300 rounded-lg p-6 text-center hover:border-indigo-500 transition-colors">
              <input
                type="file"
                id="emirates-id-back-upload"
                accept="image/*"
                className="hidden"
                onChange={handleBackFileChange}
                disabled={isProcessing}
              />
              
              {backUploading || backOcrProcessing ? (
                <div className="flex flex-col items-center gap-2 py-2">
                  <Loader className="w-8 h-8 text-indigo-500 animate-spin" />
                  <div className="text-xs font-medium text-indigo-700">
                    {backUploading ? 'Uploading...' : 'Scanning...'}
                  </div>
                </div>
              ) : (
                <>
                  <Camera className="w-10 h-10 text-indigo-400 mx-auto mb-2" />
                  <label
                    htmlFor="emirates-id-back-upload"
                    className="cursor-pointer text-sm text-indigo-600 hover:text-indigo-700 font-medium block"
                  >
                    Upload Back
                  </label>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG (max 10MB)</p>
                </>
              )}
            </div>
          ) : (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-700">Back uploaded</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setBackFile(null);
                    setBackUrl('');
                    setExtractedData(prev => {
                      const { emirates_id_back_url, occupation, sponsor, place_of_issue, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="text-xs text-gray-500 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Validation Failed Error */}
      {validationFailed && (
        <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
          <div className="flex items-start gap-3">
            <X className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-red-700 mb-2">
                ❌ Document Verification Failed
              </div>
              <div className="text-sm text-red-600 mb-3">
                This document does not match the previously uploaded documents. All documents must belong to the same person.
              </div>
              <Button
                onClick={() => {
                  setFrontFile(null);
                  setBackFile(null);
                  setFrontUrl('');
                  setBackUrl('');
                  setExtractedData({});
                  setValidationFailed(false);
                  setManualEntry(false);
                  setErrors({});
                }}
                variant="destructive"
                className="w-full"
                type="button"
              >
                Upload Correct Documents
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Extracted/Manual Fields - Show after at least one upload */}
      {((frontFile || frontUrl) || (backFile || backUrl)) && !validationFailed && !isProcessing && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-700 font-medium">
              {manualEntry ? 'Document(s) uploaded - Please verify/enter details' : 'Document scanned - Review details below'}
            </span>
          </div>

          {/* Front Side Fields Section */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="text-sm font-semibold text-purple-800 mb-3">📋 Front Side Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Emirates ID Number *</Label>
                <Input
                  value={extractedData.emirates_id || ''}
                  onChange={(e) => handleFieldChange('emirates_id', e.target.value)}
                  placeholder="784-YYYY-XXXXXXX-X"
                  className={errors.emirates_id ? 'border-red-500' : ''}
                />
                {errors.emirates_id && (
                  <p className="text-xs text-red-500 mt-1">{errors.emirates_id}</p>
                )}
              </div>

              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={extractedData.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Enter full name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={extractedData.date_of_birth || ''}
                  onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
                  className={errors.date_of_birth ? 'border-red-500' : ''}
                />
                {errors.date_of_birth && (
                  <p className="text-xs text-red-500 mt-1">{errors.date_of_birth}</p>
                )}
              </div>

              <div>
                <Label>Nationality</Label>
                <Input
                  value={extractedData.nationality || ''}
                  onChange={(e) => handleFieldChange('nationality', e.target.value)}
                  placeholder="Enter nationality"
                />
              </div>

              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={extractedData.emirates_id_issue_date || ''}
                  onChange={(e) => handleFieldChange('emirates_id_issue_date', e.target.value)}
                />
              </div>

              <div>
                <Label>Expiry Date *</Label>
                <Input
                  type="date"
                  value={extractedData.emirates_id_expiry || ''}
                  onChange={(e) => handleFieldChange('emirates_id_expiry', e.target.value)}
                  className={errors.emirates_id_expiry ? 'border-red-500' : ''}
                />
                {errors.emirates_id_expiry && (
                  <p className="text-xs text-red-500 mt-1">{errors.emirates_id_expiry}</p>
                )}
              </div>
            </div>
          </div>

          {/* Back Side Fields Section */}
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <h4 className="text-sm font-semibold text-indigo-800 mb-3">📋 Back Side Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Occupation</Label>
                <Input
                  value={extractedData.occupation || ''}
                  onChange={(e) => handleFieldChange('occupation', e.target.value)}
                  placeholder="Enter occupation"
                />
              </div>

              <div>
                <Label>Place of Issue</Label>
                <Input
                  value={extractedData.place_of_issue || ''}
                  onChange={(e) => handleFieldChange('place_of_issue', e.target.value)}
                  placeholder="Enter place of issue"
                />
              </div>

              <div className="col-span-2">
                <Label>Sponsor (from Emirates ID)</Label>
                <Input
                  value={extractedData.sponsor || ''}
                  onChange={(e) => handleFieldChange('sponsor', e.target.value)}
                  placeholder="Sponsor name from Emirates ID"
                />
                <p className="text-xs text-gray-500 mt-1">This is the sponsor mentioned on the Emirates ID back</p>
              </div>
            </div>
          </div>

          {/* Employer Selection Section */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="text-sm font-semibold text-amber-800 mb-3">🏢 Employer (Pre-Registered Lessee) *</h4>
            <div className="space-y-3">
              {/* Selected Employer Display */}
              {extractedData.lessee_id && (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-300">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-gray-800">{extractedData.lessee_name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setExtractedData(prev => ({
                        ...prev,
                        lessee_id: '',
                        lessee_name: '',
                        employer: ''
                      }));
                    }}
                    className="text-xs text-gray-500 hover:text-red-500"
                  >
                    Change
                  </button>
                </div>
              )}

              {/* Employer Dropdown with Search */}
              {!extractedData.lessee_id && (
                <div className="space-y-2">
                  {/* Search Input */}
                  <div className="relative">
                    <Input
                      value={employerSearch}
                      onChange={(e) => setEmployerSearch(e.target.value)}
                      placeholder="🔍 Search lessees by company name..."
                      className={`${errors.employer ? 'border-red-500' : ''}`}
                    />
                  </div>
                  
                  {/* Dropdown List - Always Visible */}
                  <div className="border border-gray-300 rounded-lg bg-white max-h-48 overflow-y-auto shadow-inner">
                    {filteredLessees.length > 0 ? (
                      filteredLessees.map(lessee => (
                        <button
                          key={lessee.id}
                          type="button"
                          onClick={() => handleEmployerSelect(lessee.id)}
                          className="w-full px-4 py-3 text-left hover:bg-amber-100 border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-800">{lessee.company_name}</div>
                              {lessee.owner_name && (
                                <div className="text-xs text-gray-500">Owner: {lessee.owner_name}</div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">Select →</div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-center">
                        <p className="text-sm text-gray-500">
                          {employerSearch 
                            ? `No lessees found matching "${employerSearch}"`
                            : 'No registered lessees found'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Please register the lessee first from the Lessees page</p>
                      </div>
                    )}
                  </div>
                  
                  {errors.employer && (
                    <p className="text-xs text-red-500">{errors.employer}</p>
                  )}
                </div>
              )}

              <p className="text-xs text-amber-700">
                <strong>Note:</strong> The employer must be a pre-registered lessee in the system. 
                If not listed, please register the lessee first from the Lessees page.
              </p>
            </div>
          </div>

          {/* Complete Button */}
          <Button
            onClick={handleComplete}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
            type="button"
            disabled={!bothUploaded}
          >
            {bothUploaded ? 'Complete Emirates ID Step →' : 'Please upload both Front and Back'}
          </Button>

          {/* Re-upload option */}
          <button
            type="button"
            onClick={() => {
              setFrontFile(null);
              setBackFile(null);
              setFrontUrl('');
              setBackUrl('');
              setExtractedData({});
              setManualEntry(false);
              setErrors({});
              setValidationFailed(false);
              setEmployerSearch('');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 w-full"
          >
            Start over - Upload different documents
          </button>
        </div>
      )}
    </div>
  );
};

// Passport Step Component
const PassportStep = ({ data, onComplete, editingId, wizardData }) => {
  const [uploading, setUploading] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [file, setFile] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(data?.document_url || '');
  const [extractedData, setExtractedData] = useState(data || {});
  const [manualEntry, setManualEntry] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationFailed, setValidationFailed] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!validateDocument(selectedFile)) return;

    setFile(selectedFile);
    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('entity_type', 'rider');
      uploadFormData.append('document_type', 'passport');
      
      if (editingId) {
        uploadFormData.append('entity_id', editingId);
      }

      const uploadResponse = await axios.post(
        `${API}/upload`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000 // 60 second timeout
        }
      );

      const serverFileUrl = uploadResponse.data.url;
      setDocumentUrl(serverFileUrl);
      setUploading(false); // Set uploading to false before starting OCR
      await processOCR(selectedFile, serverFileUrl);

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('File upload failed. Please try again.');
      setUploading(false);
      setFile(null);
    }
  };

  const processOCR = async (file, url) => {
    setOcrProcessing(true);
    const processingToast = toast.loading('🚀 AI Vision scanning Passport...');
    const OCR_TIMEOUT = 30000; // 30 seconds timeout

    try {
      // Use new Vision OCR API (GPT-4 Vision - much faster)
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/ocr/vision/passport`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        },
        timeout: OCR_TIMEOUT
      });

      toast.dismiss(processingToast);

      if (response.data.success && response.data.data) {
        const ocrData = response.data.data;
        
        // Map OCR response to our format
        const parsed = {
          name: `${ocrData.given_names || ''} ${ocrData.surname || ''}`.trim(),
          passport_number: ocrData.passport_number || '',
          nationality: ocrData.nationality || '',
          date_of_birth: ocrData.date_of_birth || '',
          expiry_date: ocrData.expiry_date || '',
          issue_date: ocrData.issue_date || '',
          gender: ocrData.gender || '',
          place_of_birth: ocrData.place_of_birth || ''
        };
        
        const newData = (parsed.name || parsed.passport_number) 
          ? { ...parsed, document_url: url }
          : { document_url: url };

        // Validate immediately after OCR completes
        const validationError = validateAgainstPreviousDocs(newData, wizardData, 'Passport');
        
        if (validationError) {
          setValidationFailed(true);
          setExtractedData({ document_url: url });
          toast.error('Document verification failed', {
            description: validationError,
            duration: 10000,
            style: {
              background: '#FEE2E2',
              border: '2px solid #DC2626',
              padding: '16px'
            }
          });
          return;
        }

        // No validation errors - proceed
        setValidationFailed(false);
        
        if (parsed.name || parsed.passport_number) {
          setExtractedData({ ...parsed, document_url: url });
          toast.success('✅ Passport scanned successfully!');
        } else {
          setManualEntry(true);
          setExtractedData({ document_url: url });
          toast.warning('Unable to extract data. Please enter manually.');
        }
      } else {
        throw new Error(response.data.error || 'OCR failed');
      }

    } catch (error) {
      console.error('OCR failed:', error);
      toast.dismiss(processingToast);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('⏱️ OCR timed out after 30 seconds. Please try again.');
      } else {
        toast.error('OCR scanning failed. Please enter information manually.');
      }
      setManualEntry(true);
      setExtractedData({ document_url: url });
    } finally {
      setOcrProcessing(false);
    }
  };

  const validateDocument = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file format! Please upload JPG or PNG images only.');
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large! Maximum size: 10MB`);
      return false;
    }

    const minSize = 50 * 1024;
    if (file.size < minSize) {
      toast.error('File too small! Minimum size: 50KB');
      return false;
    }

    return true;
  };

  const handleFieldChange = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateFields = () => {
    const newErrors = {};

    if (!extractedData.passport_number || extractedData.passport_number.trim() === '') {
      newErrors.passport_number = 'Passport number is required';
    }

    if (!extractedData.passport_expiry) {
      newErrors.passport_expiry = 'Expiry date is required';
    } else {
      const expiryDate = new Date(extractedData.passport_expiry);
      if (expiryDate < new Date()) {
        newErrors.passport_expiry = 'Passport has expired';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleComplete = () => {
    // Check if we have a document - check file state, documentUrl, or extractedData.document_url
    const hasDocument = file || documentUrl || extractedData.document_url;
    if (!hasDocument) {
      toast.error('Please upload a document first');
      return;
    }

    if (!validateFields()) {
      toast.error('Please fix the errors before continuing');
      return;
    }

    // Ensure document_url is in the data we're passing
    const completeData = {
      ...extractedData,
      document_url: documentUrl || extractedData.document_url || ''
    };
    onComplete(completeData);
  };

  return (
    <div className="space-y-6">
      {!file && (
        <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
          <input
            type="file"
            id="passport-upload"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading || ocrProcessing}
          />
          
          {uploading || ocrProcessing ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <Loader className="w-12 h-12 text-purple-500 animate-spin" />
              <div className="text-sm font-medium text-purple-700">
                {uploading ? 'Uploading...' : 'Scanning document...'}
              </div>
            </div>
          ) : (
            <>
              <Camera className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <label
                htmlFor="passport-upload"
                className="cursor-pointer text-lg text-purple-600 hover:text-purple-700 font-medium block mb-2"
              >
                Upload Passport
              </label>
              <p className="text-sm text-gray-500">Click to select or drag and drop</p>
              <p className="text-xs text-gray-400 mt-2">JPG, PNG (max 10MB)</p>
            </>
          )}
        </div>
      )}

      {file && !ocrProcessing && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-700 font-medium">
              {manualEntry ? 'Document uploaded - Please enter details manually' : 'Document scanned successfully - Review details below'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Passport Number *</Label>
              <Input
                value={extractedData.passport_number || ''}
                onChange={(e) => handleFieldChange('passport_number', e.target.value)}
                placeholder="Enter passport number"
                className={errors.passport_number ? 'border-red-500' : ''}
              />
              {errors.passport_number && (
                <p className="text-xs text-red-500 mt-1">{errors.passport_number}</p>
              )}
            </div>

            <div>
              <Label>Issue Date</Label>
              <Input
                type="date"
                value={extractedData.passport_issue_date || ''}
                onChange={(e) => handleFieldChange('passport_issue_date', e.target.value)}
              />
            </div>

            <div>
              <Label>Expiry Date *</Label>
              <Input
                type="date"
                value={extractedData.passport_expiry || ''}
                onChange={(e) => handleFieldChange('passport_expiry', e.target.value)}
                className={errors.passport_expiry ? 'border-red-500' : ''}
              />
              {errors.passport_expiry && (
                <p className="text-xs text-red-500 mt-1">{errors.passport_expiry}</p>
              )}
            </div>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
            type="button"
          >
            Complete Passport Step →
          </Button>

          <button
            type="button"
            onClick={() => {
              setFile(null);
              setDocumentUrl('');
              setExtractedData({});
              setManualEntry(false);
              setErrors({});
            }}
            className="text-sm text-gray-500 hover:text-gray-700 w-full"
          >
            Upload a different document
          </button>
        </div>
      )}
    </div>
  );
};

// License Step Component
const LicenseStep = ({ data, onComplete, editingId, wizardData }) => {
  const [uploading, setUploading] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [file, setFile] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(data?.document_url || '');
  const [extractedData, setExtractedData] = useState(data || {});
  const [manualEntry, setManualEntry] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationFailed, setValidationFailed] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!validateDocument(selectedFile)) return;

    setFile(selectedFile);
    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('entity_type', 'rider');
      uploadFormData.append('document_type', 'license');
      
      if (editingId) {
        uploadFormData.append('entity_id', editingId);
      }

      const uploadResponse = await axios.post(
        `${API}/upload`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000 // 60 second timeout
        }
      );

      const serverFileUrl = uploadResponse.data.url;
      setDocumentUrl(serverFileUrl);
      setUploading(false); // Set uploading to false before starting OCR
      await processOCR(selectedFile, serverFileUrl);

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('File upload failed. Please try again.');
      setUploading(false);
      setFile(null);
    }
  };

  const processOCR = async (file, url) => {
    setOcrProcessing(true);
    const processingToast = toast.loading('🚀 AI Vision scanning Driving License...');
    const OCR_TIMEOUT = 30000; // 30 seconds timeout

    try {
      // Use new Vision OCR API (GPT-4 Vision - much faster)
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/ocr/vision/driving-license`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        },
        timeout: OCR_TIMEOUT
      });

      toast.dismiss(processingToast);

      if (response.data.success && response.data.data) {
        const ocrData = response.data.data;
        
        // Map OCR response to our format
        const parsed = {
          name: ocrData.name || '',
          license_number: ocrData.license_number || '',
          date_of_birth: ocrData.date_of_birth || '',
          issue_date: ocrData.issue_date || '',
          expiry_date: ocrData.expiry_date || '',
          nationality: ocrData.nationality || '',
          license_class: ocrData.license_class || '',
          place_of_issue: ocrData.place_of_issue || ''
        };
        
        if (parsed.name || parsed.license_number) {
          setExtractedData({ ...parsed, document_url: url });
          toast.success('✅ Driving License scanned successfully!');
        } else {
          setManualEntry(true);
          setExtractedData({ document_url: url });
          toast.warning('Unable to extract data. Please enter manually.');
        }
      } else {
        throw new Error(response.data.error || 'OCR failed');
      }

    } catch (error) {
      console.error('OCR failed:', error);
      toast.dismiss(processingToast);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('⏱️ OCR timed out after 30 seconds. Please try again.');
      } else {
        toast.error('OCR scanning failed. Please enter information manually.');
      }
      setManualEntry(true);
      setExtractedData({ document_url: url });
    } finally {
      setOcrProcessing(false);
    }
  };

  const validateDocument = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file format! Please upload JPG or PNG images only.');
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large! Maximum size: 10MB`);
      return false;
    }

    const minSize = 50 * 1024;
    if (file.size < minSize) {
      toast.error('File too small! Minimum size: 50KB');
      return false;
    }

    return true;
  };

  const handleFieldChange = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateFields = () => {
    const newErrors = {};

    if (!extractedData.license_number || extractedData.license_number.trim() === '') {
      newErrors.license_number = 'License number is required';
    }

    if (!extractedData.license_expiry) {
      newErrors.license_expiry = 'Expiry date is required';
    } else {
      const expiryDate = new Date(extractedData.license_expiry);
      if (expiryDate < new Date()) {
        newErrors.license_expiry = 'License has expired';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleComplete = () => {
    // Check if we have a document - check file state, documentUrl, or extractedData.document_url
    const hasDocument = file || documentUrl || extractedData.document_url;
    if (!hasDocument) {
      toast.error('Please upload a document first');
      return;
    }

    if (!validateFields()) {
      toast.error('Please fix the errors before continuing');
      return;
    }

    // Ensure document_url is in the data we're passing
    const completeData = {
      ...extractedData,
      document_url: documentUrl || extractedData.document_url || ''
    };
    
    // Debug logging
    console.log('=== LICENSE STEP COMPLETE ===');
    console.log('documentUrl state:', documentUrl);
    console.log('extractedData.document_url:', extractedData.document_url);
    console.log('Final completeData.document_url:', completeData.document_url);
    
    onComplete(completeData);
  };

  return (
    <div className="space-y-6">
      {!file && (
        <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
          <input
            type="file"
            id="license-upload"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading || ocrProcessing}
          />
          
          {uploading || ocrProcessing ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <Loader className="w-12 h-12 text-purple-500 animate-spin" />
              <div className="text-sm font-medium text-purple-700">
                {uploading ? 'Uploading...' : 'Scanning document...'}
              </div>
            </div>
          ) : (
            <>
              <Camera className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <label
                htmlFor="license-upload"
                className="cursor-pointer text-lg text-purple-600 hover:text-purple-700 font-medium block mb-2"
              >
                Upload Driving License
              </label>
              <p className="text-sm text-gray-500">Click to select or drag and drop</p>
              <p className="text-xs text-gray-400 mt-2">JPG, PNG (max 10MB)</p>
            </>
          )}
        </div>
      )}

      {file && !ocrProcessing && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-700 font-medium">
              {manualEntry ? 'Document uploaded - Please enter details manually' : 'Document scanned successfully - Review details below'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>License Number *</Label>
              <Input
                value={extractedData.license_number || ''}
                onChange={(e) => handleFieldChange('license_number', e.target.value)}
                placeholder="Enter license number"
                className={errors.license_number ? 'border-red-500' : ''}
              />
              {errors.license_number && (
                <p className="text-xs text-red-500 mt-1">{errors.license_number}</p>
              )}
            </div>

            <div>
              <Label>Issue Date</Label>
              <Input
                type="date"
                value={extractedData.license_issue_date || ''}
                onChange={(e) => handleFieldChange('license_issue_date', e.target.value)}
              />
            </div>

            <div>
              <Label>Expiry Date *</Label>
              <Input
                type="date"
                value={extractedData.license_expiry || ''}
                onChange={(e) => handleFieldChange('license_expiry', e.target.value)}
                className={errors.license_expiry ? 'border-red-500' : ''}
              />
              {errors.license_expiry && (
                <p className="text-xs text-red-500 mt-1">{errors.license_expiry}</p>
              )}
            </div>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
            type="button"
          >
            Complete License Step →
          </Button>

          <button
            type="button"
            onClick={() => {
              setFile(null);
              setDocumentUrl('');
              setExtractedData({});
              setManualEntry(false);
              setErrors({});
            }}
            className="text-sm text-gray-500 hover:text-gray-700 w-full"
          >
            Upload a different document
          </button>
        </div>
      )}
    </div>
  );
};

// Photo Step Component
const PhotoStep = ({ data, onComplete, editingId }) => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(data?.document_url || '');

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!validateDocument(selectedFile)) return;

    setFile(selectedFile);
    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('entity_type', 'rider');
      uploadFormData.append('document_type', 'photo');
      
      if (editingId) {
        uploadFormData.append('entity_id', editingId);
      }

      const uploadResponse = await axios.post(
        `${API}/upload`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000 // 60 second timeout
        }
      );

      const serverFileUrl = uploadResponse.data.url;
      setDocumentUrl(serverFileUrl);
      toast.success('Photo uploaded successfully!');

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Photo upload failed. Please try again.');
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const validateDocument = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file format! Please upload JPG or PNG images only.');
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large! Maximum size: 10MB`);
      return false;
    }

    return true;
  };

  const handleComplete = () => {
    // Photo is optional, but if file was selected but upload failed, show error
    const hasDocument = documentUrl || file;
    if (file && !documentUrl) {
      toast.error('Photo upload in progress. Please wait.');
      return;
    }
    onComplete({ document_url: documentUrl });
  };

  return (
    <div className="space-y-6">
      {!file && (
        <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
          <input
            type="file"
            id="photo-upload"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <Loader className="w-12 h-12 text-purple-500 animate-spin" />
              <div className="text-sm font-medium text-purple-700">Uploading photo...</div>
            </div>
          ) : (
            <>
              <Camera className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <label
                htmlFor="photo-upload"
                className="cursor-pointer text-lg text-purple-600 hover:text-purple-700 font-medium block mb-2"
              >
                Upload Profile Photo
              </label>
              <p className="text-sm text-gray-500">Click to select or drag and drop</p>
              <p className="text-xs text-gray-400 mt-2">JPG, PNG (max 10MB) - Optional</p>
            </>
          )}
        </div>
      )}

      {file && !uploading && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-700 font-medium">Photo uploaded successfully!</span>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
            type="button"
          >
            Complete Upload Process →
          </Button>

          <button
            type="button"
            onClick={() => {
              setFile(null);
              setDocumentUrl('');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 w-full"
          >
            Upload a different photo
          </button>
        </div>
      )}
    </div>
  );
};

// Helper function to validate against previous documents
const validateAgainstPreviousDocs = (currentData, wizardData, currentDocType) => {
  const previousDocs = [];
  
  if (wizardData.emiratesId?.name && currentDocType !== 'Emirates ID') {
    previousDocs.push({ type: 'Emirates ID', ...wizardData.emiratesId });
  }
  if (wizardData.passport?.name && currentDocType !== 'Passport') {
    previousDocs.push({ type: 'Passport', ...wizardData.passport });
  }
  if (wizardData.license?.name && currentDocType !== 'Driving License') {
    previousDocs.push({ type: 'Driving License', ...wizardData.license });
  }

  if (previousDocs.length === 0) return null;

  // Validate name
  if (currentData.name) {
    for (const prevDoc of previousDocs) {
      if (prevDoc.name) {
        if (!areNamesSimilar(currentData.name, prevDoc.name)) {
          return `Name mismatch: "${currentData.name}" does not match "${prevDoc.name}" from ${prevDoc.type}. All documents must belong to the same person.`;
        }
      }
    }
  }

  // Validate date of birth
  if (currentData.date_of_birth) {
    for (const prevDoc of previousDocs) {
      if (prevDoc.date_of_birth && currentData.date_of_birth !== prevDoc.date_of_birth) {
        return `Date of birth mismatch: ${currentData.date_of_birth} does not match ${prevDoc.date_of_birth} from ${prevDoc.type}. All documents must belong to the same person.`;
      }
    }
  }

  return null;
};

// Helper to check name similarity
const areNamesSimilar = (name1, name2) => {
  const normalize = (name) => name.toLowerCase().trim().replace(/\s+/g, ' ');
  const n1 = normalize(name1);
  const n2 = normalize(name2);

  if (n1 === n2) return true;

  const parts1 = n1.split(' ');
  const parts2 = n2.split(' ');
  const firstName1 = parts1[0];
  const lastName1 = parts1[parts1.length - 1];
  const firstName2 = parts2[0];
  const lastName2 = parts2[parts2.length - 1];

  if (firstName1 === firstName2 && lastName1 === lastName2) return true;
  if (n1.includes(n2) || n2.includes(n1)) return true;

  const matchingParts = parts1.filter(p => parts2.includes(p)).length;
  if (matchingParts >= 2) return true;

  return false;
};

// OCR Parsing Functions (extracted from Riders.js)
const parseEmiratesIDText = (text) => {
  const extracted = {};
  
  // Emirates ID Number format: 784-YYYY-NNNNNNN-N or without dashes
  const idPatterns = [
    /(?:ID\s*Number|IDN|Identity\s*Number|Card\s*Number)[:\s\/]*([78]\d{2}[\s-]?\d{4}[\s-]?\d{7,8}[\s-]?\d{1})/i,
    /([78]\d{2}[\s-]\d{4}[\s-]\d{7,8}[\s-]\d{1})/,
    /([78]\d{14,15})/,
    /(\d{3}-\d{4}-\d{7}-\d{1})/
  ];
  
  for (const pattern of idPatterns) {
    const match = text.match(pattern);
    if (match) {
      let idNumber = match[1].replace(/[^\d]/g, '');
      if (idNumber.length >= 15) {
        idNumber = `${idNumber.slice(0,3)}-${idNumber.slice(3,7)}-${idNumber.slice(7,14)}-${idNumber.slice(14,15)}`;
        extracted.emirates_id = idNumber;
        break;
      }
    }
  }
  
  // Name extraction
  const namePatterns = [
    /Name[:\s]+([A-Z][A-Za-z\s]+)/i,
    /(?:^|\n)([A-Z][A-Z\s]{8,})/m,
    /([A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/
  ];
  
  for (const pattern of namePatterns) {
    const match = text.match(pattern);
    if (match && !match[1].includes('EMIRATES') && !match[1].includes('UNITED') && !match[1].includes('IDENTITY')) {
      extracted.name = match[1].trim();
      break;
    }
  }
  
  // Nationality extraction
  const nationalityPatterns = [
    /Nationality[:\s]+([A-Z][A-Za-z\s]+)/i,
    /(?:^|\n)(INDIAN|PAKISTANI|BANGLADESHI|FILIPINO|EGYPTIAN|JORDANIAN|SYRIAN|SRI\s*LANKAN|NEPALESE|UAE|EMIRATI)/mi
  ];
  
  for (const pattern of nationalityPatterns) {
    const match = text.match(pattern);
    if (match) {
      extracted.nationality = match[1].trim();
      break;
    }
  }
  
  // Date of Birth
  const dobPatterns = [
    /(?:Date\s*of\s*Birth|DOB|Birth\s*Date|Born)[:\s]*(\d{1,2}[\/\-\.\s]\d{1,2}[\/\-\.\s]\d{4})/i,
    /Birth[:\s]*(\d{1,2}[\/\-\.\s]\d{1,2}[\/\-\.\s]\d{4})/i,
    /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.](19|20)\d{2})/
  ];
  
  for (const pattern of dobPatterns) {
    const match = text.match(pattern);
    if (match) {
      const dateStr = match[1];
      const parts = dateStr.split(/[\/\-\.\s]+/);
      if (parts.length >= 3) {
        const [day, month, year] = parts;
        if (year && year.length === 4 && parseInt(year) >= 1950 && parseInt(year) <= 2010) {
          extracted.date_of_birth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          break;
        }
      }
    }
  }
  
  // Extract ALL dates for Issue and Expiry
  const allDates = [];
  const dateRegex1 = /\b(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})\b/g;
  let match;
  
  while ((match = dateRegex1.exec(text)) !== null) {
    const day = match[1];
    const month = match[2];
    const year = match[3];
    
    if (parseInt(month) >= 1 && parseInt(month) <= 12 && 
        parseInt(day) >= 1 && parseInt(day) <= 31 &&
        parseInt(year) >= 1950 && parseInt(year) <= 2040) {
      allDates.push({
        raw: match[0],
        day: day,
        month: month,
        year: year,
        formatted: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
        timestamp: new Date(year, month - 1, day).getTime()
      });
    }
  }
  
  allDates.sort((a, b) => a.timestamp - b.timestamp);
  
  const now = new Date().getTime();
  const dobTimestamp = extracted.date_of_birth ? new Date(extracted.date_of_birth).getTime() : 0;
  const remainingDates = allDates.filter(d => Math.abs(d.timestamp - dobTimestamp) > 86400000);
  
  // Find Issue Date (past date, after 2010)
  const issueDateCandidate = remainingDates.find(d => {
    const year = parseInt(d.year);
    return year >= 2010 && year <= 2024 && d.timestamp < now;
  });
  
  if (issueDateCandidate) {
    extracted.emirates_id_issue_date = issueDateCandidate.formatted;
  }
  
  // Find Expiry Date (future date, after 2024)
  const expiryDateCandidate = remainingDates.find(d => {
    const year = parseInt(d.year);
    return year >= 2024 && year <= 2035 && d.timestamp > now;
  });
  
  if (expiryDateCandidate) {
    extracted.emirates_id_expiry = expiryDateCandidate.formatted;
  }
  
  // Employer/Sponsor extraction
  const employerPatterns = [
    /Employer[:\s]+([A-Z][A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION)?)/i,
    /Sponsor[:\s]+([A-Z][A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION)?)/i,
    /Company[:\s]+([A-Z][A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION)?)/i
  ];
  
  for (const pattern of employerPatterns) {
    const match = text.match(pattern);
    if (match) {
      extracted.employer = match[1].trim();
      break;
    }
  }
  
  return extracted;
};

// Parse Emirates ID Back Side text for occupation, sponsor, place of issue
const parseEmiratesIDBackText = (text) => {
  const extracted = {};
  
  console.log('Emirates ID Back OCR Text:', text);
  
  // Occupation extraction patterns
  const occupationPatterns = [
    /Occupation[:\s]+([A-Za-z\s\-\/]+)/i,
    /Profession[:\s]+([A-Za-z\s\-\/]+)/i,
    /Job[:\s]+([A-Za-z\s\-\/]+)/i,
    /المهنة[:\s]*([A-Za-z\s\-\/]+)/i,
    /(?:^|\n)(DRIVER|ENGINEER|TECHNICIAN|WORKER|SALESMAN|MANAGER|SUPERVISOR|ACCOUNTANT|SECRETARY|CLERK|LABORER|LABOURER|DELIVERY|HELPER|RIDER)[^\n]*/mi
  ];
  
  for (const pattern of occupationPatterns) {
    const match = text.match(pattern);
    if (match) {
      let occupation = match[1] || match[0];
      occupation = occupation.trim().replace(/[:\s]+$/, '');
      // Filter out common false positives
      if (occupation && !occupation.match(/EMIRATES|UNITED|IDENTITY|CARD|AUTHORITY/i)) {
        extracted.occupation = occupation;
        break;
      }
    }
  }
  
  // Sponsor/Employer extraction patterns (back side)
  const sponsorPatterns = [
    /Sponsor[:\s]+([A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION|EST|ESTABLISHMENT)?)/i,
    /Employer[:\s]+([A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION|EST|ESTABLISHMENT)?)/i,
    /الكفيل[:\s]*([A-Za-z\s&.,-]+)/i,
    /جهة\s*العمل[:\s]*([A-Za-z\s&.,-]+)/i,
    /Company[:\s]+([A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION|EST|ESTABLISHMENT)?)/i
  ];
  
  for (const pattern of sponsorPatterns) {
    const match = text.match(pattern);
    if (match) {
      let sponsor = match[1].trim();
      // Filter out common false positives
      if (sponsor && !sponsor.match(/EMIRATES|UNITED|IDENTITY|CARD|AUTHORITY/i) && sponsor.length > 2) {
        extracted.sponsor = sponsor;
        break;
      }
    }
  }
  
  // Place of Issue extraction patterns
  const placePatterns = [
    /Place\s*of\s*Issue[:\s]+([A-Za-z\s]+)/i,
    /Issued\s*(?:at|in)[:\s]+([A-Za-z\s]+)/i,
    /مكان\s*الإصدار[:\s]*([A-Za-z\s]+)/i,
    /(?:^|\n)(DUBAI|ABU\s*DHABI|SHARJAH|AJMAN|FUJAIRAH|RAS\s*AL\s*KHAIMAH|UMM\s*AL\s*QUWAIN)[^\n]*/mi
  ];
  
  for (const pattern of placePatterns) {
    const match = text.match(pattern);
    if (match) {
      let place = match[1] || match[0];
      place = place.trim().replace(/[:\s]+$/, '');
      // Filter out common false positives
      if (place && !place.match(/EMIRATES|UNITED|IDENTITY|CARD|NUMBER/i)) {
        extracted.place_of_issue = place;
        break;
      }
    }
  }
  
  return extracted;
};

const parsePassportText = (text) => {
  const extracted = {};
  
  // Log the raw text for debugging
  console.log('Passport OCR Text:', text);
  
  // Passport number patterns - expanded for various passport formats
  const passportPatterns = [
    // Indian passport format: Letter followed by 7 digits
    /(?:Passport\s*No\.?|No\.?)[:\s]*([A-Z]\d{7})/i,
    /(?:^|\s)([A-Z]\d{7})(?:\s|$)/m,
    // Generic patterns
    /Passport\s*No[:\s]*([A-Z0-9]+)/i,
    /Passport\s*Number[:\s]*([A-Z0-9]+)/i,
    /(?:^|\n)([A-Z]{1,2}\d{6,9})/m,
    /No[:\s]*([A-Z0-9]{6,})/i,
    // MRZ line pattern (passport number is typically in first line after P<)
    /P<[A-Z]{3}[A-Z<]+<<+([A-Z0-9]+)/,
    // Look for 7-9 digit alphanumeric starting with letter
    /(?:^|\s)([A-Z][A-Z0-9]{6,8})(?:\s|$)/m
  ];
  
  for (const pattern of passportPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const candidate = match[1].trim();
      // Validate it looks like a passport number (letter followed by digits or alphanumeric)
      if (/^[A-Z][A-Z0-9]{5,8}$/i.test(candidate)) {
        extracted.passport_number = candidate.toUpperCase();
        console.log('Extracted passport number:', extracted.passport_number);
        break;
      }
    }
  }
  
  // Name extraction
  const surnameMatch = text.match(/Surname[:\s]+([A-Z][A-Za-z\s]+)/i);
  const givenMatch = text.match(/Given\s*Names?[:\s]+([A-Z][A-Za-z\s]+)/i);
  
  if (surnameMatch && givenMatch) {
    extracted.name = `${givenMatch[1].trim()} ${surnameMatch[1].trim()}`;
  } else {
    const namePatterns = [
      /Name[:\s]+([A-Z][A-Za-z\s]+)/i,
      /(?:^|\n)([A-Z][A-Z\s]{8,})/m
    ];
    
    for (const pattern of namePatterns) {
      const match = text.match(pattern);
      if (match && !match[1].includes('PASSPORT') && !match[1].includes('REPUBLIC')) {
        extracted.name = match[1].trim();
        break;
      }
    }
  }
  
  // Date of Birth
  const months = { 
    JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
    JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
    JANUARY: '01', FEBRUARY: '02', MARCH: '03', APRIL: '04', JUNE: '06',
    JULY: '07', AUGUST: '08', SEPTEMBER: '09', OCTOBER: '10', NOVEMBER: '11', DECEMBER: '12'
  };
  
  const dobPatterns = [
    /(?:Date\s*of\s*Birth|DOB|Birth\s*Date)[:\s]*(\d{1,2})\s+([A-Z]{3,})\s+(\d{4})/i,
    /(?:Date\s*of\s*Birth|DOB)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    /Birth[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    // Indian passport format: DD/MM/YYYY or DD-MM-YYYY
    /(\d{2})[\/\-](\d{2})[\/\-](\d{4})/
  ];
  
  for (const pattern of dobPatterns) {
    const match = text.match(pattern);
    if (match) {
      if (match.length === 4 && isNaN(match[2])) {
        // Format: DD MONTH YYYY
        const day = match[1].padStart(2, '0');
        const monthStr = match[2].toUpperCase().slice(0, 3);
        const month = months[monthStr] || '01';
        const year = match[3];
        extracted.date_of_birth = `${year}-${month}-${day}`;
      } else if (match.length === 4) {
        // Format: DD/MM/YYYY captured as separate groups
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3];
        if (parseInt(year) > 1900 && parseInt(year) < 2100) {
          extracted.date_of_birth = `${year}-${month}-${day}`;
        }
      } else {
        const [day, month, year] = match[1].split(/[\/\-\.]/);
        if (year && year.length === 4) {
          extracted.date_of_birth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      break;
    }
  }
  
  // Issue Date
  const issueDatePatterns = [
    /(?:Date\s*of\s*Issue|Issue\s*Date|Issued|Issue)[:\s]*(\d{1,2})\s+([A-Z]{3,})\s+(\d{4})/i,
    /(?:Date\s*of\s*Issue|Issue\s*Date|Issued)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    /Issue[:\s]*(\d{2})[\/\-](\d{2})[\/\-](\d{4})/i
  ];
  
  for (const pattern of issueDatePatterns) {
    const match = text.match(pattern);
    if (match) {
      if (match.length === 4 && isNaN(match[2])) {
        const day = match[1].padStart(2, '0');
        const monthStr = match[2].toUpperCase().slice(0, 3);
        const month = months[monthStr] || '01';
        const year = match[3];
        extracted.passport_issue_date = `${year}-${month}-${day}`;
      } else if (match.length === 4) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3];
        if (parseInt(year) > 1900 && parseInt(year) < 2100) {
          extracted.passport_issue_date = `${year}-${month}-${day}`;
        }
      } else {
        const [day, month, year] = match[1].split(/[\/\-\.]/);
        if (year && year.length === 4) {
          extracted.passport_issue_date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      break;
    }
  }
  
  // Expiry Date
  const expiryPatterns = [
    /(?:Date\s*of\s*Expiry|Expiry\s*Date|Expires|Expiry|Valid\s*Until)[:\s]*(\d{1,2})\s+([A-Z]{3,})\s+(\d{4})/i,
    /(?:Date\s*of\s*Expiry|Expiry\s*Date|Expires)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    /Valid\s*Until[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    /Expiry[:\s]*(\d{2})[\/\-](\d{2})[\/\-](\d{4})/i
  ];
  
  for (const pattern of expiryPatterns) {
    const match = text.match(pattern);
    if (match) {
      if (match.length === 4 && isNaN(match[2])) {
        const day = match[1].padStart(2, '0');
        const monthStr = match[2].toUpperCase().slice(0, 3);
        const month = months[monthStr] || '01';
        const year = match[3];
        extracted.passport_expiry = `${year}-${month}-${day}`;
      } else if (match.length === 4) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3];
        if (parseInt(year) > 1900 && parseInt(year) < 2100) {
          extracted.passport_expiry = `${year}-${month}-${day}`;
        }
      } else {
        const [day, month, year] = match[1].split(/[\/\-\.]/);
        if (year && year.length === 4) {
          extracted.passport_expiry = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      break;
    }
  }
  
  console.log('Passport extracted data:', extracted);
  return extracted;
};

const parseDriverLicenseText = (text) => {
  const extracted = {};
  
  console.log('Driving License OCR Text:', text);
  
  // License number patterns
  const licensePatterns = [
    /License\s*No[:\s]*([A-Z0-9-]+)/i,
    /Lic[:\s]*No[:\s]*([A-Z0-9-]+)/i,
    /Traffic\s*File[:\s]*([A-Z0-9-]+)/i,
    /(?:^|\n)(\d{9,})/m
  ];
  
  for (const pattern of licensePatterns) {
    const match = text.match(pattern);
    if (match) {
      extracted.license_number = match[1].trim();
      break;
    }
  }
  
  // Name patterns
  const namePatterns = [
    /Name[:\s]+([A-Z][A-Za-z\s]+)/i,
    /(?:^|\n)([A-Z][A-Z\s]{5,})/m
  ];
  
  for (const pattern of namePatterns) {
    const match = text.match(pattern);
    if (match && !match[1].includes('EMIRATES') && !match[1].includes('LICENSE')) {
      extracted.name = match[1].trim();
      break;
    }
  }
  
  // First, extract all dates with their context/labels
  const allDates = [];
  
  // Find dates with labels
  const labeledDatePatterns = [
    { pattern: /(?:Date\s*of\s*Birth|D\.?O\.?B\.?|Birth\s*Date)[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i, type: 'dob' },
    { pattern: /(?:Issue\s*Date|Date\s*of\s*Issue|Issued)[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i, type: 'issue' },
    { pattern: /(?:Expiry\s*Date|Valid\s*Until|Expires|Exp)[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i, type: 'expiry' }
  ];
  
  for (const { pattern, type } of labeledDatePatterns) {
    const match = text.match(pattern);
    if (match) {
      allDates.push({ dateStr: match[1], type, labeled: true });
    }
  }
  
  // Helper to parse date string to ISO format and get year
  const parseDate = (dateStr) => {
    const parts = dateStr.split(/[\/\-]/);
    if (parts.length === 3) {
      let [day, month, year] = parts;
      // Handle potential month/day swap
      if (parseInt(day) > 12 && parseInt(month) <= 12) {
        // Day is > 12, so format is DD/MM/YYYY
      } else if (parseInt(month) > 12 && parseInt(day) <= 12) {
        // Month is > 12, swap them
        [day, month] = [month, day];
      }
      return {
        iso: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
        year: parseInt(year)
      };
    }
    return null;
  };
  
  // Assign labeled dates
  for (const dateInfo of allDates) {
    const parsed = parseDate(dateInfo.dateStr);
    if (parsed) {
      if (dateInfo.type === 'dob') {
        // Only accept DOB if it's a reasonable birth year (1950-2010)
        if (parsed.year >= 1950 && parsed.year <= 2010) {
          extracted.date_of_birth = parsed.iso;
        }
      } else if (dateInfo.type === 'issue') {
        extracted.license_issue_date = parsed.iso;
      } else if (dateInfo.type === 'expiry') {
        extracted.license_expiry = parsed.iso;
      }
    }
  }
  
  // If no labeled dates found, try to find unlabeled dates and classify them intelligently
  if (!extracted.license_issue_date || !extracted.license_expiry) {
    const genericDatePattern = /(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/g;
    let match;
    const unlabeledDates = [];
    
    while ((match = genericDatePattern.exec(text)) !== null) {
      const parsed = parseDate(match[1]);
      if (parsed && !allDates.some(d => d.dateStr === match[1])) {
        unlabeledDates.push(parsed);
      }
    }
    
    // Sort dates by year
    unlabeledDates.sort((a, b) => a.year - b.year);
    
    // Classify unlabeled dates:
    // - Years 1950-2010 could be DOB
    // - Years 2015-current could be issue date
    // - Years current-2040 could be expiry date
    const currentYear = new Date().getFullYear();
    
    for (const parsed of unlabeledDates) {
      if (!extracted.license_expiry && parsed.year >= currentYear && parsed.year <= 2040) {
        // Future date = expiry
        extracted.license_expiry = parsed.iso;
      } else if (!extracted.license_issue_date && parsed.year >= 2015 && parsed.year <= currentYear) {
        // Recent past date = issue date
        extracted.license_issue_date = parsed.iso;
      }
      // NOTE: We do NOT auto-assign DOB from unlabeled dates - too risky for misclassification
    }
  }
  
  return extracted;
};

export default DocumentUploadWizard;
